// src/pages/JewelleryPlans/Plans/PlanDetails/PauseHistoryTab.jsx

import React, { useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";
import { format, differenceInDays } from "date-fns";
import {
  ClockIcon,
  CheckCircleIcon,
  XCircleIcon,
  BanIcon,
  CalendarIcon,
  DocumentTextIcon,
} from "@heroicons/react/outline";
import { getPauseHistoryThunk } from "../../../../store/thunks/jewelleryPlan/planThunks";
import Badge from "../../../../components/common/Badge";

const PauseHistoryTab = ({ plan }) => {
  const dispatch = useDispatch();
  const { pauseHistory, pauseEligibility, pauseLoading } = useSelector(
    (state) => state.jewelleryPlan?.plans || {}
  );

  useEffect(() => {
    if (plan?._id) {
      dispatch(getPauseHistoryThunk(plan._id));
    }
  }, [plan?._id, dispatch]);

  const getStatusIcon = (status) => {
    switch (status) {
      case "ACTIVE":
      case "COMPLETED":
        return <CheckCircleIcon className="h-5 w-5 text-green-600" />;
      case "REJECTED":
        return <XCircleIcon className="h-5 w-5 text-red-600" />;
      case "CANCELLED":
        return <BanIcon className="h-5 w-5 text-gray-600" />;
      case "EXPIRED":
        return <ClockIcon className="h-5 w-5 text-yellow-600" />;
      case "REQUESTED":
      case "PENDING_APPROVAL":
        return <ClockIcon className="h-5 w-5 text-blue-600" />;
      default:
        return <ClockIcon className="h-5 w-5 text-gray-600" />;
    }
  };

  const getStatusBadgeVariant = (status) => {
    switch (status) {
      case "ACTIVE":
        return "success";
      case "COMPLETED":
        return "success";
      case "REJECTED":
        return "danger";
      case "CANCELLED":
        return "secondary";
      case "EXPIRED":
        return "warning";
      case "REQUESTED":
      case "PENDING_APPROVAL":
        return "info";
      default:
        return "secondary";
    }
  };

  const getReasonLabel = (reason) => {
    const labels = {
      FINANCIAL_TEMPORARY: "Temporary Financial Issue",
      MEDICAL_EMERGENCY: "Medical Emergency",
      JOB_LOSS: "Job Loss",
      TRAVEL: "Travel",
      FAMILY_EMERGENCY: "Family Emergency",
      MATERNITY: "Maternity",
      EDUCATION: "Education",
      BUSINESS_LOSS: "Business Loss",
      NATURAL_DISASTER: "Natural Disaster",
      OTHER: "Other",
    };
    return labels[reason] || reason;
  };

  if (pauseLoading) {
    return (
      <div className="flex justify-center items-center py-12">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Pause Metrics Summary */}
      {pauseEligibility && (
        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <h3 className="text-lg font-medium text-gray-900 mb-4">
            Pause Quota & Eligibility
          </h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <div className="p-4 bg-blue-50 rounded-lg">
              <p className="text-sm text-blue-600 mb-1">Total Paused</p>
              <p className="text-2xl font-bold text-blue-900">
                {plan.pauseMetrics?.totalMonthsPaused || 0} / 6
              </p>
              <p className="text-xs text-blue-600 mt-1">months used</p>
            </div>
            <div className="p-4 bg-green-50 rounded-lg">
              <p className="text-sm text-green-600 mb-1">Remaining Quota</p>
              <p className="text-2xl font-bold text-green-900">
                {pauseEligibility.remainingPauseMonths || 0}
              </p>
              <p className="text-xs text-green-600 mt-1">months left</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg">
              <p className="text-sm text-purple-600 mb-1">Pause Instances</p>
              <p className="text-2xl font-bold text-purple-900">
                {plan.pauseMetrics?.totalPauseInstances || 0} / 3
              </p>
              <p className="text-xs text-purple-600 mt-1">times used</p>
            </div>
            <div className="p-4 bg-yellow-50 rounded-lg">
              <p className="text-sm text-yellow-600 mb-1">Current Status</p>
              <p className="text-lg font-bold text-yellow-900">
                {plan.pauseMetrics?.currentlyPaused ? "Paused" : "Active"}
              </p>
              {pauseEligibility.nextEligibleDate && (
                <p className="text-xs text-yellow-600 mt-1">
                  Next eligible:{" "}
                  {format(
                    new Date(pauseEligibility.nextEligibleDate),
                    "dd MMM yyyy"
                  )}
                </p>
              )}
            </div>
          </div>

          {/* Eligibility Status */}
          <div className="mt-4">
            {pauseEligibility.eligible ? (
              <div className="flex items-center p-3 bg-green-50 border border-green-200 rounded-lg">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <p className="text-sm text-green-800">
                  Plan is eligible for pause
                </p>
              </div>
            ) : (
              <div className="flex items-center p-3 bg-red-50 border border-red-200 rounded-lg">
                <XCircleIcon className="h-5 w-5 text-red-600 mr-2" />
                <div>
                  <p className="text-sm font-medium text-red-800">
                    Plan is not eligible for pause
                  </p>
                  {pauseEligibility.reason && (
                    <p className="text-xs text-red-700 mt-1">
                      {pauseEligibility.reason}
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Pause History Timeline */}
      <div className="bg-white border border-gray-200 rounded-lg p-6">
        <h3 className="text-lg font-medium text-gray-900 mb-6">
          Pause History
        </h3>

        {!pauseHistory || pauseHistory.length === 0 ? (
          <div className="text-center py-12">
            <ClockIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
            <p className="text-gray-600">No pause history found</p>
            <p className="text-sm text-gray-500 mt-2">
              This plan has not been paused yet
            </p>
          </div>
        ) : (
          <div className="space-y-6">
            {pauseHistory.map((pause, index) => (
              <div
                key={pause.pauseId || index}
                className="relative pb-6 border-l-2 border-gray-200 pl-6 ml-3"
              >
                {/* Timeline Icon */}
                <div className="absolute -left-3 top-0 bg-white">
                  {getStatusIcon(pause.status)}
                </div>

                {/* Pause Card */}
                <div className="bg-gray-50 rounded-lg p-4 hover:bg-gray-100 transition-colors">
                  {/* Header */}
                  <div className="flex items-start justify-between mb-3">
                    <div>
                      <div className="flex items-center space-x-3">
                        <h4 className="text-sm font-semibold text-gray-900">
                          Pause #{pauseHistory.length - index}
                        </h4>
                        <Badge
                          variant={getStatusBadgeVariant(pause.status)}
                          size="sm"
                        >
                          {pause.status.replace("_", " ")}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600 mt-1">
                        {getReasonLabel(pause.reason)}
                      </p>
                    </div>
                    <span className="text-xs text-gray-500">
                      {format(new Date(pause.requestedAt), "dd MMM yyyy")}
                    </span>
                  </div>

                  {/* Dates & Duration */}
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
                    <div>
                      <p className="text-xs text-gray-600">Start Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {format(new Date(pause.startDate), "dd MMM yyyy")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">End Date</p>
                      <p className="text-sm font-medium text-gray-900">
                        {format(new Date(pause.endDate), "dd MMM yyyy")}
                      </p>
                    </div>
                    <div>
                      <p className="text-xs text-gray-600">Duration</p>
                      <p className="text-sm font-medium text-gray-900">
                        {pause.monthsPaused} month(s)
                      </p>
                    </div>
                    {pause.actualResumeDate && (
                      <div>
                        <p className="text-xs text-gray-600">Resumed On</p>
                        <p className="text-sm font-medium text-gray-900">
                          {format(
                            new Date(pause.actualResumeDate),
                            "dd MMM yyyy"
                          )}
                        </p>
                      </div>
                    )}
                  </div>

                  {/* Reason Details */}
                  {pause.reasonDetails && (
                    <div className="mb-3 p-2 bg-white rounded border border-gray-200">
                      <p className="text-xs font-medium text-gray-700 mb-1">
                        Details:
                      </p>
                      <p className="text-sm text-gray-800">
                        {pause.reasonDetails}
                      </p>
                    </div>
                  )}

                  {/* Maturity Impact */}
                  {pause.impactOnMaturity && (
                    <div className="mb-3 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <p className="text-xs font-medium text-blue-900 mb-2">
                        Impact on Maturity:
                      </p>
                      <div className="grid grid-cols-2 gap-3 text-sm">
                        <div>
                          <p className="text-xs text-blue-600">
                            Original Maturity
                          </p>
                          <p className="font-medium text-blue-900">
                            {format(
                              new Date(
                                pause.impactOnMaturity.originalMaturityDate
                              ),
                              "dd MMM yyyy"
                            )}
                          </p>
                        </div>
                        <div>
                          <p className="text-xs text-blue-600">New Maturity</p>
                          <p className="font-medium text-blue-900">
                            {format(
                              new Date(pause.impactOnMaturity.newMaturityDate),
                              "dd MMM yyyy"
                            )}
                          </p>
                        </div>
                      </div>
                      <p className="text-xs text-blue-700 mt-2">
                        Extended by {pause.impactOnMaturity.extensionMonths}{" "}
                        month(s)
                      </p>
                    </div>
                  )}

                  {/* Installments Affected */}
                  {pause.installmentsAffected &&
                    pause.installmentsAffected.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 mb-1">
                          Installments Affected:
                        </p>
                        <p className="text-sm text-gray-800">
                          {pause.installmentsAffected.length} installment(s)
                          postponed
                        </p>
                      </div>
                    )}

                  {/* Documents */}
                  {pause.documentsProvided &&
                    pause.documentsProvided.length > 0 && (
                      <div className="mb-3">
                        <p className="text-xs font-medium text-gray-700 mb-2">
                          Supporting Documents:
                        </p>
                        <div className="flex flex-wrap gap-2">
                          {pause.documentsProvided.map((doc, docIndex) => (
                            <span
                              key={docIndex}
                              className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-white border border-gray-200 text-gray-800"
                            >
                              <DocumentTextIcon className="h-3 w-3 mr-1" />
                              {doc}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}

                  {/* Approval/Rejection Info */}
                  {pause.status === "ACTIVE" || pause.status === "COMPLETED" ? (
                    <div className="flex items-center text-xs text-green-700 bg-green-50 p-2 rounded">
                      <CheckCircleIcon className="h-4 w-4 mr-1" />
                      {pause.approvedBy ? (
                        <span>
                          Approved{" "}
                          {pause.approvedAt &&
                            `on ${format(
                              new Date(pause.approvedAt),
                              "dd MMM yyyy"
                            )}`}
                        </span>
                      ) : (
                        <span>Auto-approved</span>
                      )}
                    </div>
                  ) : pause.status === "REJECTED" ? (
                    <div className="text-xs text-red-700 bg-red-50 p-2 rounded">
                      <div className="flex items-center mb-1">
                        <XCircleIcon className="h-4 w-4 mr-1" />
                        <span>
                          Rejected{" "}
                          {pause.rejectedAt &&
                            `on ${format(
                              new Date(pause.rejectedAt),
                              "dd MMM yyyy"
                            )}`}
                        </span>
                      </div>
                      {pause.rejectionReason && (
                        <p className="ml-5">{pause.rejectionReason}</p>
                      )}
                    </div>
                  ) : null}

                  {/* Notes */}
                  {pause.notes && (
                    <div className="mt-3 p-2 bg-yellow-50 border border-yellow-200 rounded">
                      <p className="text-xs font-medium text-yellow-900 mb-1">
                        Notes:
                      </p>
                      <p className="text-sm text-yellow-800">{pause.notes}</p>
                    </div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default PauseHistoryTab;
